'use client'

import { useAddRelatedFieldsToEventDetail } from '../../../../../MyStichtingOpenHelpers/src/static'
import React, { type FC, type ReactNode, useRef } from 'react'

interface Props {
    children: ReactNode
    triggerAdditionalValidationOnFields?: string[]
}
/**
 * @description
 * This is an event modifier for RequestValidationEvent
 *
 * IMPORTANT! this component does not have to be used when using the Form component {@link ../Form.tsx}.
 * That component has sane defaults for this functionality. The reason you may want this component, is
 * when you want validation on inputA to be triggered when inputB changes, but not the other
 * way around
 *
 * RelatedFields retrieves all input fields from it's container and adds their name to details.relatedFields.
 *
 * Optionally, additional fields can be defined in the triggerAdditionalValidationOnFields parameter. Those
 * fields will be triggered to validate, but when those fields themselves change, the fields contained within
 * this RelatedFields component will not be triggered to validate. To do that, surround those fields with
 * another RelatedFields component and add these fieldnames in their triggerAdditionalValidationOnFields parameter.
 * (see example A and B)
 *
 * This component can be placed at any depth in the react tree, as long as it only contains input fields that target
 * the same form (see example C)
 *
 * This component is rendered in a div that has a display:contents style applied. Therefore it can be used as if
 * it only renders a fragment.
 *
 * ## about event modifiers (not a web api)
 * In this project, an event modifier captures bubbling CustomEvents of a certain type and modifies it's detail property
 * For more info about the mechanics, read about event bubbling and propagation
 * {@link https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Building_blocks/Event_bubbling}
 *
 * @example A -- with triggerAdditionalValidationOnFields
 * In this example:
 *  - When InputFieldA changes, these fields will be validated: ['field-a', 'field-b', 'field-c']
 *  - When InputFieldB changes, these fields will be validated: ['field-a', 'field-b', 'field-c']
 *  - When InputFieldC changes, these fields will be validated: ['field-c']
 * ```
 *  <ValidateFields on="change">
 *
 *      <RelatedFields triggerAdditionalValidationOnFields="field-c">
 *          <InputFieldA />
 *          <InputFieldB />
 *      </RelatedFields
 *
 *      <InputFieldC />
 *
 *  </ValidateFields>
 * ```
 *
 * @example B -- without parameters
 * In this example:
 *  - When InputFieldA changes, these fields will be validated: ['field-a', 'field-b']
 *  - When InputFieldB changes, these fields will be validated: ['field-a', 'field-b']
 *  - When InputFieldC changes, these fields will be validated: ['field-c']
 * ```
 *  <ValidateFields on="change">
 *
 *      <RelatedFields>
 *          <InputFieldA />
 *          <InputFieldB />
 *      </RelatedFields
 *
 *      <InputFieldC />
 *
 *  </ValidateFields>
 * ```
 *
 * @example C -- tree-depth order doesn't matter (compare this with example A or B)
 * ```
 *  <RelatedFields>
 *      <ValidateFields on="change">
 *
 *          <InputFieldA />
 *          <InputFieldB />
 *
 *      </ValidateFields>
 *
 *      <ValidateFields on="input">
 *          <InputFieldC />
 *      </ValidateFields>
 *  </RelatedFields
 * ```
 *
 * @param triggerAdditionalValidationOnFields -- fields that need to be validated when one of the fields
 * contained in this RelatedFields component triggers a validation request
 * */
export const RelatedFields: FC<Props> = ({ children, triggerAdditionalValidationOnFields }) => {
    const container = useRef<HTMLDivElement>(null)
    useAddRelatedFieldsToEventDetail(container, [triggerAdditionalValidationOnFields ?? []], true)
    return (
        <div className="contents" ref={container}>
            {children}
        </div>
    )
}
